import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Images, Loader2, Trash2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KATEGORIEN = [
  ["hygiene", "Kategorie A: Hygiene & Mobilität"],
  ["lohnfertigung", "Kategorie B: Lohnfertigung"],
];
const KATEGORIE_LABELS = { hygiene: "Hygiene & Mobilität", lohnfertigung: "Lohnfertigung" };

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [titel, setTitel] = useState("");
  const [kategorie, setKategorie] = useState("hygiene");
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/galerie`);
      setItems(data);
    } catch {
      toast.error("Galerie konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) {
      toast.error("Bitte ein Bild auswählen (JPG oder PNG)");
      return;
    }
    if (!titel.trim()) {
      toast.error("Bitte eine kurze Beschreibung eingeben");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("bild", file);
      fd.append("titel", titel.trim());
      fd.append("kategorie", kategorie);
      await axios.post(`${API}/galerie`, fd, { withCredentials: true });
      toast.success("Bild wurde hochgeladen");
      setFile(null);
      setTitel("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bild wirklich aus der Galerie entfernen?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/galerie/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Bild entfernt");
    } catch {
      toast.error("Bild konnte nicht entfernt werden");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div data-testid="gallery-manager">
      <div className="border border-[#E3E8EC] bg-white rounded-2xl p-6 lg:p-8 shadow-[0_4px_40px_rgba(17,24,39,0.04)]">
        <h2 className="text-base font-semibold tracking-tight text-[var(--ci-grey)]">Neues Bild hochladen</h2>
        <p className="mt-1 text-sm text-[var(--ci-grey)]">JPG oder PNG, max. 15 MB. Erscheint sofort in der Kundenansicht der gewählten Kategorie.</p>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_1fr_auto] gap-4 items-end">
          <div>
            <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] mb-2">Bilddatei</div>
            <label
              className="flex items-center gap-3 h-11 px-4 rounded-xl border border-dashed border-[#C3CCD4] hover:border-[var(--ci-turquoise)] bg-[#F7F9FA] cursor-pointer transition-colors text-sm text-[var(--ci-grey)] truncate"
              data-testid="gallery-file-label"
            >
              <ImagePlus size={16} className="text-[var(--ci-blue)] shrink-0" />
              <span className="truncate">{file ? file.name : "Bild auswählen …"}</span>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                data-testid="gallery-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
          <div>
            <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] mb-2">Beschreibung</div>
            <input
              type="text"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              maxLength={120}
              placeholder="z. B. Edelstahl-Spendergehäuse"
              data-testid="gallery-title-input"
              className="w-full h-11 px-4 rounded-xl border border-[#D9E0E6] bg-white text-sm text-[var(--ci-grey)] placeholder:text-[var(--ci-grey)] focus:outline-none focus:ring-2 focus:ring-[var(--ci-turquoise)]"
            />
          </div>
          <div>
            <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)] mb-2">Kategorie</div>
            <Select value={kategorie} onValueChange={setKategorie}>
              <SelectTrigger className="h-11 bg-white border-[#D9E0E6] text-[var(--ci-grey)] rounded-xl focus:ring-2 focus:ring-[var(--ci-turquoise)]" data-testid="gallery-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                {KATEGORIEN.map(([k, v]) => (
                  <SelectItem key={k} value={k} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]" data-testid={`gallery-category-option-${k}`}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={upload}
            disabled={uploading}
            data-testid="gallery-upload-btn"
            className="flex items-center justify-center gap-2 h-11 px-7 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] text-white font-tech text-[11px] tracking-[0.18em] uppercase transition-colors duration-200 active:scale-[0.98] disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? "Wird hochgeladen …" : "Hochladen"}
          </button>
        </div>
      </div>

      <div className="mt-8 border border-[#E3E8EC] bg-white rounded-2xl p-6 lg:p-8 shadow-[0_4px_40px_rgba(17,24,39,0.04)]">
        {loading ? (
          <div className="py-14 flex items-center justify-center gap-3 text-[var(--ci-grey)]" data-testid="gallery-admin-loading">
            <Loader2 size={18} className="animate-spin" /> Lade Galerie …
          </div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center" data-testid="gallery-admin-empty">
            <Images size={32} className="mx-auto text-[#C3CCD4]" />
            <p className="mt-4 text-sm text-[var(--ci-grey)]">Noch keine Bilder in der Galerie.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="gallery-admin-grid">
            {items.map((item, i) => (
              <figure key={item.id} className="group relative rounded-xl overflow-hidden border border-[#E3E8EC] bg-[#F7F9FA]" data-testid={`gallery-admin-item-${i}`}>
                <img src={`${BACKEND}${item.bild_url}`} alt={item.titel} loading="lazy" className="block w-full aspect-[4/3] object-cover" />
                <button
                  onClick={() => remove(item.id)}
                  disabled={deleting === item.id}
                  data-testid={`gallery-delete-${i}`}
                  aria-label="Bild entfernen"
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 border border-[#E3E8EC] text-[var(--ci-grey)] hover:text-[#DC2626] hover:border-[#DC2626]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60"
                >
                  {deleting === item.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
                <figcaption className="px-3.5 py-3 bg-white border-t border-[#EEF1F4]">
                  <div className="text-xs font-medium text-[var(--ci-grey)] truncate">{item.titel}</div>
                  <div className="mt-1 font-tech text-[9px] tracking-[0.15em] uppercase text-[var(--ci-grey)]">{KATEGORIE_LABELS[item.kategorie] || item.kategorie}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
